import React, {useContext, useEffect, useState} from "react";
import TrackVASContext from "../../../TrackVASContext";
import {PageTitle} from "../../../../_metronic/layout/core";
import {getMonthlyOverview, recordOrgPaymentBatch, deleteOrgPayment} from "../../generic/crud";
import {showToast, TOAST_FAILURE, TOAST_SUCCESS} from "../../../utils/toast/Toast";
import {simpleDateFormat} from "../../../utils/Utils";
import {showConfirmDialog} from "../../../generic-components/ConfirmDialog";
import {useNavigate} from "react-router-dom";
import {RecordPaymentDialog} from "./RecordPaymentDialog";

const MONTH_FULL_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function getCurrentMonthKey() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthLabel(monthKey) {
    const [year, month] = monthKey.split('-');
    const monthIdx = parseInt(month, 10) - 1;
    return `${MONTH_FULL_NAMES[monthIdx]} ${year}`;
}

function getMonthOptions() {
    const options = [];
    const now = new Date();
    // Go back 12 months and forward 1 month
    for (let i = -12; i <= 1; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        options.push({key, label: formatMonthLabel(key)});
    }
    return options;
}

export function SAMonthlyOverviewPage() {
    const myData = useContext(TrackVASContext);
    const navigate = useNavigate();
    const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthKey());
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [expandedOrg, setExpandedOrg] = useState(null);

    // For batch payment from this page
    const [showModal, setShowModal] = useState(false);
    const [paymentOrgId, setPaymentOrgId] = useState(null);
    const [paymentMonthlyAmount, setPaymentMonthlyAmount] = useState(0);

    const profileBreadCrumbs = [
        {title: "Home", path: "/sadashboard", isSeparator: false, isActive: false},
        {title: "", path: "", isSeparator: true, isActive: false},
        {title: "Billing", path: "/super-admin/billing", isSeparator: false, isActive: false},
        {title: "", path: "", isSeparator: true, isActive: false},
    ];

    const monthOptions = getMonthOptions();

    function loadData(month) {
        setIsLoading(true);
        getMonthlyOverview(month || selectedMonth).then(({data}) => {
            setData(data);
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
    }

    function handleMonthChange(newMonth) {
        setSelectedMonth(newMonth);
        setExpandedOrg(null);
        loadData(newMonth);
    }

    function navigateMonth(direction) {
        const [year, month] = selectedMonth.split('-').map(Number);
        const d = new Date(year, month - 1 + direction, 1);
        const newKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        handleMonthChange(newKey);
    }

    function openQuickPay(org) {
        setPaymentOrgId(org.id);
        setPaymentMonthlyAmount(org.totalMonthlyBilling || org.monthlyBillingAmount || 0);
        setShowModal(true);
    }

    async function handleDeletePayment(orgId, paymentId) {
        const result = await showConfirmDialog("Do you really want to delete this payment record?");
        if (result) {
            deleteOrgPayment(orgId, paymentId).then(() => {
                showToast("Payment record deleted!", TOAST_SUCCESS);
                loadData();
            }).catch((err) => {
                showToast(err.response?.data || "Error deleting payment.", TOAST_FAILURE);
            });
        }
    }

    function handlePaymentSaved() {
        setShowModal(false);
        setPaymentOrgId(null);
        loadData();
    }

    useEffect(() => {
        myData.setBaseData({toolBarButtons: []});
        loadData();
        return () => {
            myData.setBaseData({toolBarButtons: []});
        };
    }, []);

    const paidCount = data.filter(d => d.paid).length;
    const pendingCount = data.filter(d => !d.paid).length;
    const totalExpected = data.reduce((sum, d) => sum + (d.totalMonthlyBilling || 0), 0);
    const totalCollected = data.reduce((sum, d) => sum + (d.totalPaid || 0), 0);

    function getBillingStatusBadge(status) {
        switch (status) {
            case "ACTIVE": return <span className="badge badge-light-success fs-9">Active</span>;
            case "WARNING": return <span className="badge badge-light-warning fs-9">Warning</span>;
            case "SUSPENDED": return <span className="badge badge-light-danger fs-9">Suspended</span>;
            case "CANCELLED": return <span className="badge badge-light-dark fs-9">Cancelled</span>;
            case "DEMO":
            default: return <span className="badge badge-light-info fs-9">Demo</span>;
        }
    }

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>Monthly Payment Overview</PageTitle>

            {/* Month Selector */}
            <div className="card mb-5">
                <div className="card-body py-5">
                    <div className="d-flex align-items-center justify-content-center gap-4">
                        <button className="btn btn-sm btn-icon btn-light" onClick={() => navigateMonth(-1)}>
                            <i className="fa fa-chevron-left"></i>
                        </button>
                        <div className="text-center">
                            <h2 className="fw-bold mb-0">{formatMonthLabel(selectedMonth)}</h2>
                        </div>
                        <button className="btn btn-sm btn-icon btn-light" onClick={() => navigateMonth(1)}>
                            <i className="fa fa-chevron-right"></i>
                        </button>
                    </div>
                    {/* Quick month jump */}
                    <div className="d-flex flex-wrap justify-content-center gap-2 mt-4">
                        {monthOptions.slice(-6).map(opt => (
                            <button
                                key={opt.key}
                                className={`btn btn-sm ${opt.key === selectedMonth ? 'btn-primary' : 'btn-light'}`}
                                onClick={() => handleMonthChange(opt.key)}>
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            {!isLoading && (
                <div className="row g-5 mb-5">
                    <div className="col-lg-3 col-md-6">
                        <div className="card">
                            <div className="card-body py-5 text-center">
                                <span className="text-muted fw-semibold fs-7">Total Organizations</span>
                                <div className="text-dark fw-bold fs-1">{data.length}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="card">
                            <div className="card-body py-5 text-center">
                                <span className="text-muted fw-semibold fs-7">Paid</span>
                                <div className="text-success fw-bold fs-1">{paidCount}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="card">
                            <div className="card-body py-5 text-center">
                                <span className="text-muted fw-semibold fs-7">Pending</span>
                                <div className="text-danger fw-bold fs-1">{pendingCount}</div>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                        <div className="card">
                            <div className="card-body py-5 text-center">
                                <span className="text-muted fw-semibold fs-7">Collected / Expected</span>
                                <div className="fw-bold fs-4">
                                    <span className="text-success">₹{totalCollected.toLocaleString()}</span>
                                    <span className="text-muted"> / ₹{totalExpected.toLocaleString()}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Table */}
            <div className="card card-body">
                {isLoading && <div className="text-center py-10"><span>Loading...</span></div>}
                {!isLoading && (
                    <>
                        {/* Pending Section */}
                        {pendingCount > 0 && (
                            <>
                                <h5 className="fw-bold text-danger mb-4">
                                    <i className="fa fa-times-circle text-danger me-2"></i>
                                    Pending ({pendingCount})
                                </h5>
                                <div className="table-responsive mb-8">
                                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                                        <thead>
                                            <tr className="fw-bold text-muted">
                                                <th className="min-w-200px">Organization</th>
                                                <th className="min-w-100px">Status</th>
                                                <th className="min-w-120px">Expected Amount</th>
                                                <th className="min-w-150px text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.filter(d => !d.paid).map((org) => (
                                                <tr key={org.id}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="symbol symbol-35px me-3">
                                                                <div className="symbol-label bg-light-danger">
                                                                    <i className="fa fa-times text-danger"></i>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <span className="text-dark fw-bold fs-6">{org.name}</span>
                                                                {org.phone && <span className="text-muted fw-semibold d-block fs-7">{org.phone}</span>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{getBillingStatusBadge(org.billingStatus)}</td>
                                                    <td>
                                                        <span className="text-dark fw-bold fs-6">
                                                            ₹{(org.totalMonthlyBilling || 0).toLocaleString()}
                                                        </span>
                                                        {org.branchCount > 0 && (
                                                            <span className="text-muted fw-semibold d-block fs-8">
                                                                ₹{(org.monthlyBillingAmount || 0).toLocaleString()} x {org.branchCount} branches
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="text-end">
                                                        <button
                                                            className="btn btn-sm btn-primary me-2"
                                                            onClick={() => openQuickPay(org)}>
                                                            <i className="fa fa-check me-1"></i>Record Payment
                                                        </button>
                                                        <button
                                                            className="btn btn-sm btn-light"
                                                            onClick={() => navigate("/super-admin/billing/" + org.id + "/payments", {
                                                                state: {orgName: org.name, monthlyAmount: org.totalMonthlyBilling || org.monthlyBillingAmount || 0, createdOn: null}
                                                            })}>
                                                            <i className="fa fa-calendar me-1"></i>History
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {/* Paid Section */}
                        {paidCount > 0 && (
                            <>
                                <h5 className="fw-bold text-success mb-4">
                                    <i className="fa fa-check-circle text-success me-2"></i>
                                    Paid ({paidCount})
                                </h5>
                                <div className="table-responsive">
                                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                                        <thead>
                                            <tr className="fw-bold text-muted">
                                                <th className="min-w-200px">Organization</th>
                                                <th className="min-w-100px">Status</th>
                                                <th className="min-w-120px">Amount Paid</th>
                                                <th className="min-w-150px">Reference</th>
                                                <th className="min-w-150px text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.filter(d => d.paid).map((org) => {
                                                const isExpanded = expandedOrg === org.id;
                                                return (
                                                    <React.Fragment key={org.id}>
                                                        <tr>
                                                            <td>
                                                                <div className="d-flex align-items-center">
                                                                    <div className="symbol symbol-35px me-3">
                                                                        <div className="symbol-label bg-light-success">
                                                                            <i className="fa fa-check text-success"></i>
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <span className="text-dark fw-bold fs-6">{org.name}</span>
                                                                        {org.phone && <span className="text-muted fw-semibold d-block fs-7">{org.phone}</span>}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td>{getBillingStatusBadge(org.billingStatus)}</td>
                                                            <td>
                                                                <span className="text-success fw-bold fs-6">
                                                                    ₹{(org.totalPaid || 0).toLocaleString()}
                                                                </span>
                                                                {org.payments && org.payments.length > 1 && (
                                                                    <span className="text-muted fs-9 d-block">
                                                                        ({org.payments.length} payments)
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                {org.payments && org.payments.length === 1 ? (
                                                                    <span className="text-muted fw-semibold">
                                                                        {org.payments[0].referenceNumber || "-"}
                                                                    </span>
                                                                ) : (
                                                                    <span
                                                                        className="text-primary fw-semibold cursor-pointer"
                                                                        style={{cursor: 'pointer'}}
                                                                        onClick={() => setExpandedOrg(isExpanded ? null : org.id)}>
                                                                        {isExpanded ? 'Hide details' : 'View details'}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="text-end">
                                                                <button
                                                                    className="btn btn-sm btn-light-primary me-2"
                                                                    onClick={() => openQuickPay(org)}>
                                                                    <i className="fa fa-plus me-1"></i>Add Payment
                                                                </button>
                                                                {org.payments && org.payments.length === 1 && (
                                                                    <button
                                                                        className="btn btn-sm btn-light-danger me-2"
                                                                        onClick={() => handleDeletePayment(org.id, org.payments[0].id)}>
                                                                        <i className="fa fa-trash-alt me-1"></i>Delete
                                                                    </button>
                                                                )}
                                                                <button
                                                                    className="btn btn-sm btn-light"
                                                                    onClick={() => navigate("/super-admin/billing/" + org.id + "/payments", {
                                                                        state: {orgName: org.name, monthlyAmount: org.totalMonthlyBilling || org.monthlyBillingAmount || 0, createdOn: null}
                                                                    })}>
                                                                    <i className="fa fa-calendar me-1"></i>History
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        {/* Expanded payment details */}
                                                        {isExpanded && org.payments && org.payments.map((p) => (
                                                            <tr key={p.id} className="bg-light-success">
                                                                <td className="ps-15">
                                                                    <span className="text-muted fs-7">
                                                                        Recorded by {p.recordedBy} on {p.createdOn ? simpleDateFormat(p.createdOn) : "-"}
                                                                    </span>
                                                                </td>
                                                                <td></td>
                                                                <td>
                                                                    <span className="fw-semibold fs-7">₹{(p.amount || 0).toLocaleString()}</span>
                                                                </td>
                                                                <td>
                                                                    <span className="text-muted fs-7">{p.referenceNumber || "-"}</span>
                                                                    {p.remarks && <span className="text-muted fs-8 d-block">{p.remarks}</span>}
                                                                </td>
                                                                <td className="text-end">
                                                                    <button
                                                                        className="btn btn-sm btn-icon btn-light-danger"
                                                                        onClick={() => handleDeletePayment(org.id, p.id)}>
                                                                        <i className="fa fa-trash-alt fs-7"></i>
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {data.length === 0 && (
                            <div className="text-center text-muted py-10">
                                No organizations found for this month.
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Quick pay dialog - pays for the selected month */}
            <RecordPaymentDialog
                show={showModal}
                handleClose={() => setShowModal(false)}
                orgId={paymentOrgId}
                monthlyAmount={paymentMonthlyAmount}
                selectedMonths={[selectedMonth]}
                onSave={handlePaymentSaved}
            />
        </>
    );
}
